import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getExpiredReturns, saveExpiredReturn, getNextExpiredReturnNo, getStockForExpiredReturn, getExpiredReturnById } from "../../components/Api/GmsExpiredReturn";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { Eye, X } from "lucide-react";
import StockSelectionModal from "../../components/StockSelectionModal";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
};

const ExpiredReturnPage = () => {
  const navigate = useNavigate();

  // Permissions setup
  const permissions = useMemo(() => JSON.parse(localStorage.getItem('permissions') || '[]'), []);
  const pageRoute = '/GmsExpiredReturn';
  const hasPermission = hasPagePermissionWithAdmin(permissions, pageRoute);
  const isViewOnly = hasViewOnlyPermission(permissions, pageRoute);
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, pageRoute);
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, pageRoute);
  const isAdmin = isUserAdmin();

  // Tab State: Default active tab is "2" (New Expired Return)
  const [activeTab, setActiveTab] = useState("2");

  // Filter States (List Tab)
  const [returnNoFilter, setReturnNoFilter] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState(null);
  const [toDateFilter, setToDateFilter] = useState(null);

  // Lists and Lookups
  const [expiredReturnsList, setExpiredReturnsList] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form State (Header)
  const [expiredReturnId, setExpiredReturnId] = useState(0);
  const [returnNo, setReturnNo] = useState("");
  const [returnDate, setReturnDate] = useState(new Date());
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [remarks, setRemarks] = useState("");

  // Product Selection & Details State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [details, setDetails] = useState([]);

  // Stock Selection Modal State (For Each/Individual Stock Items)
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [currentProductForStock, setCurrentProductForStock] = useState(null);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  // Detail View Modal State
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedViewReturn, setSelectedViewReturn] = useState(null);

  // Permission Guard Effect
  useEffect(() => {
    if (!hasPermission) {
      Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        text: 'You do not have permission to access this page.',
      }).then(() => {
        navigate('/GmsAdminDashboard');
      });
    }
  }, [hasPermission, navigate]);

  // Initial Data Loading
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [busRes, branchRes, supRes, prodRes, unitRes] = await Promise.all([
        getBusinesses({ PageIndex: 1, PageSize: 100 }),
        getBranches({ PageIndex: 1, PageSize: 100 }),
        getSuppliers({ PageIndex: 1, PageSize: 200 }),
        getProducts({ PageIndex: 1, PageSize: 1000 }),
        getActiveUnits()
      ]);

      const busList = busRes?.Data || busRes || [];
      const branchList = branchRes?.Data || branchRes || [];
      setBusinesses(busList);
      setBranches(branchList);
      setSuppliers(supRes?.Data || supRes || []);
      setProducts(prodRes?.Data || prodRes || []);
      setUnits(unitRes?.Data || unitRes || []);

      // Auto populate from user session
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const sessionCompanyId = authUser.companyId || user.user_Company_Id || localStorage.getItem("userCompanyId") || null;
      const sessionBranchId = authUser.branchId || user.user_Branch_Id || localStorage.getItem("userBranchId") || null;

      let defaultBus = null;
      let defaultBranch = null;

      if (sessionCompanyId && busList.length > 0) {
        const foundBus = busList.find(b => (b.business_Id || b.Business_Id) === parseInt(sessionCompanyId));
        if (foundBus) {
          defaultBus = {
            value: foundBus.business_Id || foundBus.Business_Id,
            label: foundBus.business_NameEn || foundBus.Business_NameEn || foundBus.business_NameAr || foundBus.Business_NameAr
          };
          setSelectedBusiness(defaultBus);
        }
      }

      if (sessionBranchId && branchList.length > 0) {
        const foundBranch = branchList.find(b => (b.Branch_Id || b.branch_Id) === parseInt(sessionBranchId));
        if (foundBranch) {
          defaultBranch = {
            value: foundBranch.Branch_Id || foundBranch.branch_Id,
            label: foundBranch.Branch_NameEn || foundBranch.branch_NameEn || foundBranch.Branch_NameAr || foundBranch.branch_NameAr,
            branch: foundBranch
          };
          setSelectedBranch(defaultBranch);
        }
      }

      // Generate Return Voucher No for default business
      await fetchNextReturnNo(defaultBus ? defaultBus.value : null);

      await fetchReturnsList();
    } catch (err) {
      console.error("Error loading master data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNextReturnNo = async (busId = null) => {
    try {
      const res = await getNextExpiredReturnNo(busId);
      if (res && (res.ReturnNo || res.returnNo)) {
        setReturnNo(res.ReturnNo || res.returnNo);
      } else if (typeof res === 'string') {
        setReturnNo(res);
      } else {
        setReturnNo("1");
      }
    } catch (err) {
      setReturnNo("1");
    }
  };

  const handleBusinessChange = async (opt) => {
    setSelectedBusiness(opt);
    setSelectedBranch(null); // Reset branch when company changes
    setDetails([]); // Reset line items
    await fetchNextReturnNo(opt ? opt.value : null);
  };

  const fetchReturnsList = async () => {
    try {
      setLoading(true);
      const filter = {
        ExpiredReturn_No: returnNoFilter || undefined,
        FromDate: fromDateFilter ? fromDateFilter.toISOString() : undefined,
        ToDate: toDateFilter ? toDateFilter.toISOString() : undefined,
      };
      const res = await getExpiredReturns(filter);
      setExpiredReturnsList(res?.Data || res || []);
    } catch (err) {
      console.error("Error fetching expired returns list:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper map for unit fractional check
  const unitMap = useMemo(() => {
    const map = {};
    units.forEach(u => {
      map[u.Unit_Id] = u;
    });
    return map;
  }, [units]);

  // Check if a unit allows fractional / weight
  const isFractionalUnit = (unitId) => {
    const u = unitMap[unitId];
    return u ? !!u.Unit_AllowFractional : false;
  };

  const handleAddNew = async () => {
    resetForm();
    setActiveTab("2");
    await fetchNextReturnNo(selectedBusiness ? selectedBusiness.value : null);
  };

  const resetForm = () => {
    setExpiredReturnId(0);
    setReturnNo("");
    setReturnDate(new Date());
    setSelectedBranch(null);
    setSelectedBusiness(null);
    setSelectedSupplier(null);
    setRemarks("");
    setSelectedProduct(null);
    setDetails([]);
  };

  // Handle Product Selection from dropdown
  const handleProductSelect = async (prodOption) => {
    if (!prodOption) {
      setSelectedProduct(null);
      return;
    }
    if (!selectedBranch) {
      Swal.fire({
        icon: 'warning',
        title: 'Branch Required',
        text: 'Please select a branch first before adding products.',
      });
      setSelectedProduct(null);
      return;
    }

    const prod = prodOption.product;
    const productId = prod.Product_Id || prod.product_Id;

    try {
      setStockLoading(true);
      const busId = selectedBusiness ? selectedBusiness.value : null;
      const res = await getStockForExpiredReturn(productId, selectedBranch.value, busId);
      const stocks = res?.stockItems || [];
      if (!stocks || stocks.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Out of Stock',
          text: `No stock units available for "${prod.Product_NameEn || prod.product_NameEn}".`,
        });
        setSelectedProduct(null);
        return;
      }
      setCurrentProductForStock(prod);
      setAvailableStocks(stocks);
      setStockModalOpen(true);
    } catch (err) {
      console.error("Error fetching stock units for product:", err);
    } finally {
      setStockLoading(false);
    }
  };

  // Confirm selection from Stock Modal
  // StockSelectionModal.onStockSelected returns grouped objects:
  // { price, discount, quantity, stocks: [...rawStockItems], batchNumbers, expiryDates }
  const handleConfirmStockSelection = (groupedStockItems) => {
    if (!groupedStockItems || groupedStockItems.length === 0) return;

    const prod = currentProductForStock;
    const unitId = prod.Unit_Id || prod.unit_Id || prod.PurchaseStock_Unit_Id || 0;
    const productId = prod.Product_Id || prod.product_Id;
    const productNameEn = prod.Product_NameEn || prod.product_NameEn;

    const newDetails = [];

    groupedStockItems.forEach(group => {
      // Each group has { price, discount, quantity, stocks: [rawStockItem, ...] }
      const rawStocks = group.stocks || [];
      const isFractionalGroup = rawStocks.some(s =>
        s.unit_AllowFractional || s.unitAllowFractional || s.Unit_AllowFractional
      );

      rawStocks.forEach(rawStock => {
        // API returns camelCase from ASP.NET Core JSON serialization
        const cost = parseFloat(rawStock.purchaseStock_CostPrice ?? 0);
        const discPct = parseFloat(rawStock.discountPercent ?? 0);
        const taxPct = parseFloat(rawStock.taxPercent ?? 0);
        const discAmt = (cost * discPct) / 100;
        const taxAmt = ((cost - discAmt) * taxPct) / 100;
        const qty = isFractionalGroup
          ? parseFloat(rawStock.selectedQty || group.quantity || 1)
          : 1;

        newDetails.push({
          ExpiredReturnDet_Id: 0,
          Product_Id: productId,
          Product_NameEn: productNameEn,
          Unit_Id: unitId,
          Unit_NameEn: unitMap[unitId]?.Unit_NameEn || 'Each',
          PurchaseStock_Id: rawStock.purchaseStock_Id ?? 0,
          BatchNo: rawStock.purchaseStock_BatchNo ?? '',
          ExpDate: rawStock.purchaseStock_ExpDate ?? '',
          AvailableStock: parseFloat(rawStock.purchaseStock_RemainingQty ?? qty),
          Quantity: qty,
          CostPrice: cost,
          DiscountAmount: discAmt,
          TaxPercentage: taxPct,
          TaxAmount: taxAmt,
          TotalAmount: (qty * cost) - discAmt + taxAmt,
          IsFractional: isFractionalGroup
        });
      });
    });

    setDetails(prev => [...prev, ...newDetails]);
    setStockModalOpen(false);
    setSelectedProduct(null);
    setCurrentProductForStock(null);
  };

  // Quantity Change Handler with validation against existing stock balance
  const handleQuantityChange = (index, val) => {
    const qty = parseFloat(val) || 0;
    setDetails(prev => {
      const updated = [...prev];
      const item = { ...updated[index] };

      if (qty > item.AvailableStock) {
        Swal.fire({
          icon: 'warning',
          title: 'Exceeds Stock Balance',
          text: `Entered return quantity (${qty}) cannot be greater than existing available stock balance (${item.AvailableStock}).`,
        });
        item.Quantity = item.AvailableStock;
      } else {
        item.Quantity = qty;
      }

      item.TotalAmount = (item.Quantity * item.CostPrice) - item.DiscountAmount + item.TaxAmount;
      updated[index] = item;
      return updated;
    });
  };

  // Cost / Discount / Tax Change Handlers
  const handleItemDetailChange = (index, field, val) => {
    const numVal = parseFloat(val) || 0;
    setDetails(prev => {
      const updated = [...prev];
      const item = { ...updated[index] };
      item[field] = numVal;

      if (field === 'TaxPercentage') {
        item.TaxAmount = ((item.Quantity * item.CostPrice - item.DiscountAmount) * numVal) / 100;
      }

      item.TotalAmount = (item.Quantity * item.CostPrice) - item.DiscountAmount + item.TaxAmount;
      updated[index] = item;
      return updated;
    });
  };

  // Remove Item line
  const handleRemoveDetail = (index) => {
    setDetails(prev => prev.filter((_, i) => i !== index));
  };

  // Totals calculations
  const totals = useMemo(() => {
    const totalQty = details.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0);
    const subTotal = details.reduce((sum, d) => sum + ((parseFloat(d.Quantity) || 0) * (parseFloat(d.CostPrice) || 0)), 0);
    const discountAmount = details.reduce((sum, d) => sum + (parseFloat(d.DiscountAmount) || 0), 0);
    const taxAmount = details.reduce((sum, d) => sum + (parseFloat(d.TaxAmount) || 0), 0);
    const totalAmount = subTotal - discountAmount + taxAmount;

    return { totalQty, subTotal, discountAmount, taxAmount, totalAmount };
  }, [details]);

  // Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBranch) {
      Swal.fire({ icon: 'warning', title: 'Validation Error', text: 'Please select a Branch.' });
      return;
    }
    if (details.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Validation Error', text: 'Please add at least one product item to return.' });
      return;
    }

    const payload = {
      ExpiredReturn_Id: expiredReturnId,
      ExpiredReturn_No: returnNo,
      ExpiredReturn_Date: returnDate.toISOString(),
      Branch_Id: selectedBranch.value,
      Business_Id: selectedBusiness ? selectedBusiness.value : (selectedBranch.branch ? selectedBranch.branch.Business_Id : null),
      Supplier_Id: selectedSupplier ? selectedSupplier.value : null,
      TotalQuantity: totals.totalQty,
      SubTotal: totals.subTotal,
      DiscountAmount: totals.discountAmount,
      TaxAmount: totals.taxAmount,
      TotalAmount: totals.totalAmount,
      Remarks: remarks,
      Details: details.map(d => ({
        ExpiredReturnDet_Id: d.ExpiredReturnDet_Id || 0,
        Product_Id: d.Product_Id,
        Unit_Id: d.Unit_Id,
        PurchaseStock_Id: d.PurchaseStock_Id,
        Quantity: d.Quantity,
        CostPrice: d.CostPrice,
        DiscountAmount: d.DiscountAmount || 0,
        TaxPercentage: d.TaxPercentage || 0,
        TaxAmount: d.TaxAmount || 0,
        TotalAmount: d.TotalAmount
      }))
    };

    try {
      setSubmitting(true);
      await saveExpiredReturn(payload);
      Swal.fire({
        icon: 'success',
        title: 'Saved Successfully',
        text: 'Expired return entry has been saved and stock updated.',
      });
      resetForm();
      await fetchReturnsList();
    } catch (err) {
      console.error("Error saving expired return:", err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.Message || 'Failed to save expired return entry.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // View Return Detail Handler
  const handleView = async (item) => {
    const id = item.ExpiredReturn_Id || item.expiredReturn_Id;
    if (id) {
      try {
        setLoading(true);
        const fullData = await getExpiredReturnById(id);
        setSelectedViewReturn(fullData || item);
      } catch (err) {
        console.error("Error fetching expired return details by ID:", err);
        setSelectedViewReturn(item);
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedViewReturn(item);
    }
    setViewModalOpen(true);
  };

  // Select Options
  const businessOptions = useMemo(() => businesses.map(b => ({
    value: b.business_Id || b.Business_Id,
    label: b.business_NameEn || b.Business_NameEn || b.business_NameAr || b.Business_NameAr
  })), [businesses]);

  const filteredBranches = useMemo(() => {
    if (!selectedBusiness) return branches;
    const busId = selectedBusiness.value;
    return branches.filter(b => (b.Branch_Business_Id || b.business_Id || b.Business_Id) === busId);
  }, [branches, selectedBusiness]);

  const branchOptions = useMemo(() => filteredBranches.map(b => ({
    value: b.Branch_Id || b.branch_Id,
    label: b.Branch_NameEn || b.branch_NameEn || b.Branch_NameAr || b.branch_NameAr,
    branch: b
  })), [filteredBranches]);

  const supplierOptions = useMemo(() => suppliers.map(s => ({
    value: s.supplier_Id || s.Supplier_Id,
    label: s.supplier_NameEn || s.Supplier_NameEn || s.supplier_NameAr || s.Supplier_NameAr
  })), [suppliers]);

  const productOptions = useMemo(() => products.map(p => ({
    value: p.Product_Id || p.product_Id,
    label: `${p.Product_Code || p.product_Code} - ${p.Product_NameEn || p.product_NameEn}`,
    product: p
  })), [products]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Inventory" breadcrumbItem="Expired Product Returns" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom">
                    {hasEditOrHigher && (
                      <NavItem>
                        <NavLink
                          className={activeTab === "2" ? "active" : ""}
                          onClick={() => {
                            if (activeTab !== "2") handleAddNew();
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          {expiredReturnId > 0 ? "Edit Expired Return" : "New Expired Return"}
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                        style={{ cursor: "pointer" }}
                      >
                        Expired Returns List
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="p-3">
                    {/* TAB 1: LIST TAB */}
                    <TabPane tabId="1">
                      <Row className="mb-3 align-items-end">
                        <Col md={3}>
                          <Label>Return No</Label>
                          <Input
                            type="text"
                            placeholder="Search by Return No..."
                            value={returnNoFilter}
                            onChange={(e) => setReturnNoFilter(e.target.value)}
                          />
                        </Col>
                        <Col md={3}>
                          <Label>From Date</Label>
                          <DatePicker
                            selected={fromDateFilter}
                            onChange={(date) => setFromDateFilter(date)}
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select From Date"
                          />
                        </Col>
                        <Col md={3}>
                          <Label>To Date</Label>
                          <DatePicker
                            selected={toDateFilter}
                            onChange={(date) => setToDateFilter(date)}
                            className="form-control"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select To Date"
                          />
                        </Col>
                        <Col md={3} className="d-flex gap-2">
                          <Button color="primary" onClick={fetchReturnsList}>
                            Filter
                          </Button>
                          <Button color="secondary" onClick={() => {
                            setReturnNoFilter("");
                            setFromDateFilter(null);
                            setToDateFilter(null);
                            fetchReturnsList();
                          }}>
                            Reset
                          </Button>
                        </Col>
                      </Row>

                      {loading ? (
                        <div className="text-center my-5">
                          <ClipLoader color="#556ee6" size={35} />
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table className="table-bordered table-hover align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Return No</th>
                                <th>Date</th>
                                <th>Branch</th>
                                <th>Supplier</th>
                                <th>Total Qty</th>
                                <th>Total Amount</th>
                                <th className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expiredReturnsList.length > 0 ? (
                                expiredReturnsList.map((item, index) => (
                                  <tr key={item.ExpiredReturn_Id || item.expiredReturn_Id || index}>
                                    <td>{index + 1}</td>
                                    <td><strong>{item.ExpiredReturn_No || item.expiredReturn_No}</strong></td>
                                    <td>{item.ExpiredReturn_Date || item.expiredReturn_Date ? new Date(item.ExpiredReturn_Date || item.expiredReturn_Date).toLocaleDateString() : '-'}</td>
                                    <td>{item.Branch_NameEn || item.branch_NameEn || item.Branch_Id || item.branch_Id}</td>
                                    <td>{item.Supplier_NameEn || item.supplier_NameEn || item.Supplier_Id || item.supplier_Id || '-'}</td>
                                    <td>{parseFloat(item.TotalQuantity || item.totalQuantity || 0).toFixed(2)}</td>
                                    <td>{parseFloat(item.TotalAmount || item.totalAmount || 0).toFixed(2)}</td>
                                    <td className="text-center">
                                      <Button
                                        color="info"
                                        size="sm"
                                        title="View Details"
                                        onClick={() => handleView(item)}
                                      >
                                        <Eye size={16} />
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="8" className="text-center text-muted py-4">
                                    No expired return records found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </TabPane>

                    {/* TAB 2: ADD / EDIT FORM TAB */}
                    {hasEditOrHigher && (
                      <TabPane tabId="2">
                        <Form onSubmit={handleSubmit}>
                          <Card className="border shadow-none mb-3">
                            <CardBody>
                              <h5 className="card-title mb-3">Voucher Details</h5>
                              <Row className="g-3">
                                <Col md={3}>
                                  <Label>Company / Business <span className="text-danger">*</span></Label>
                                  <Select
                                    options={businessOptions}
                                    value={selectedBusiness}
                                    onChange={handleBusinessChange}
                                    styles={selectStyles}
                                    isDisabled={isViewOnly || details.length > 0}
                                    isClearable
                                    placeholder="Select Company..."
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>Branch <span className="text-danger">*</span></Label>
                                  <Select
                                    options={branchOptions}
                                    value={selectedBranch}
                                    onChange={(opt) => {
                                      setSelectedBranch(opt);
                                      setDetails([]); // Reset items if branch changes
                                    }}
                                    styles={selectStyles}
                                    isDisabled={isViewOnly || details.length > 0 || !selectedBusiness}
                                    placeholder={selectedBusiness ? "Select Branch..." : "Select Company first..."}
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>Return Voucher No <span className="text-danger">*</span></Label>
                                  <Input
                                    type="text"
                                    value={returnNo}
                                    readOnly
                                    required
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>Return Date <span className="text-danger">*</span></Label>
                                  <DatePicker
                                    selected={returnDate}
                                    onChange={(date) => setReturnDate(date)}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                    disabled={isViewOnly}
                                  />
                                </Col>
                                <Col md={3}>
                                  <Label>Supplier</Label>
                                  <Select
                                    options={supplierOptions}
                                    value={selectedSupplier}
                                    onChange={(opt) => setSelectedSupplier(opt)}
                                    styles={selectStyles}
                                    isDisabled={isViewOnly}
                                    isClearable
                                    placeholder="Select Supplier..."
                                  />
                                </Col>
                                <Col md={12}>
                                  <Label>Remarks</Label>
                                  <Input
                                    type="textarea"
                                    rows="2"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    readOnly={isViewOnly}
                                    placeholder="Reason for expiry return..."
                                  />
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>

                          {/* PRODUCT SEARCH & SELECTION */}
                          {!isViewOnly && (
                            <Card className="border shadow-none mb-3">
                              <CardBody>
                                <h5 className="card-title mb-3">Add Product to Return</h5>
                                <Row className="align-items-center">
                                  <Col md={6}>
                                    <Label>Select Product (Each or Weight/Kg)</Label>
                                    <Select
                                      options={productOptions}
                                      value={selectedProduct}
                                      onChange={handleProductSelect}
                                      styles={selectStyles}
                                      placeholder="Type or select product..."
                                      isLoading={stockLoading}
                                    />
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          )}

                          {/* LINE ITEMS TABLE */}
                          <Card className="border shadow-none mb-3">
                            <CardBody>
                              <h5 className="card-title mb-3">Return Items Details</h5>
                              <div className="table-responsive">
                                <Table className="table-bordered align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>#</th>
                                      <th>Product</th>
                                      <th>Unit Type</th>
                                      <th>Batch / Exp Date</th>
                                      <th style={{ width: "120px" }}>Avail. Stock</th>
                                      <th style={{ width: "130px" }}>Return Qty</th>
                                      <th style={{ width: "120px" }}>Cost Price</th>
                                      <th style={{ width: "110px" }}>Discount</th>
                                      <th style={{ width: "110px" }}>Tax (%)</th>
                                      <th>Total</th>
                                      {!isViewOnly && <th className="text-center">Action</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.length > 0 ? (
                                      details.map((item, index) => (
                                        <tr key={index}>
                                          <td>{index + 1}</td>
                                          <td>{item.Product_NameEn}</td>
                                          <td>
                                            <span className={`badge ${item.IsFractional ? 'bg-info' : 'bg-primary'}`}>
                                              {item.Unit_NameEn} {item.IsFractional ? '(Weight)' : '(Each)'}
                                            </span>
                                          </td>
                                          <td>
                                            {item.BatchNo && item.ExpDate ? `Batch: ${item.BatchNo} | Exp: ${item.ExpDate}` : 
                                             item.BatchNo ? `Batch: ${item.BatchNo}` : 
                                             item.ExpDate ? `Exp: ${item.ExpDate}` : '-'}
                                          </td>
                                          <td>
                                            <span className="badge bg-secondary">{parseFloat(item.AvailableStock).toFixed(2)}</span>
                                          </td>
                                          <td>{parseFloat(item.Quantity || 0).toFixed(item.IsFractional ? 2 : 0)}</td>
                                          <td>{parseFloat(item.CostPrice || 0).toFixed(2)}</td>
                                          <td>{parseFloat(item.DiscountAmount || 0).toFixed(2)}</td>
                                          <td>{parseFloat(item.TaxPercentage || 0).toFixed(2)}%</td>
                                          <td><strong>{parseFloat(item.TotalAmount || 0).toFixed(2)}</strong></td>
                                          {!isViewOnly && (
                                            <td className="text-center">
                                              <Button
                                                color="danger"
                                                outline
                                                size="sm"
                                                onClick={() => handleRemoveDetail(index)}
                                              >
                                                <X size={16} />
                                              </Button>
                                            </td>
                                          )}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={isViewOnly ? "10" : "11"} className="text-center text-muted py-3">
                                          No items added to return list yet.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                  {details.length > 0 && (
                                    <tfoot className="table-light fw-bold">
                                      <tr>
                                        <td colSpan="5" className="text-end">Total:</td>
                                        <td>{totals.totalQty.toFixed(2)}</td>
                                        <td>{totals.subTotal.toFixed(2)}</td>
                                        <td>{totals.discountAmount.toFixed(2)}</td>
                                        <td>{totals.taxAmount.toFixed(2)}</td>
                                        <td colSpan={isViewOnly ? "1" : "2"}>
                                          <span className="text-success h6 mb-0">{totals.totalAmount.toFixed(2)}</span>
                                        </td>
                                      </tr>
                                    </tfoot>
                                  )}
                                </Table>
                              </div>
                            </CardBody>
                          </Card>

                          {/* ACTION BUTTONS */}
                          {!isViewOnly && (
                            <div className="d-flex justify-content-end gap-2 mb-4">
                              <Button color="secondary" onClick={() => setActiveTab("1")}>
                                Cancel
                              </Button>
                              <Button color="primary" type="submit" disabled={submitting || details.length === 0}>
                                {submitting ? <ClipLoader size={18} color="#fff" /> : "Submit Expired Return"}
                              </Button>
                            </div>
                          )}
                        </Form>
                      </TabPane>
                    )}
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* STOCK SELECTION MODAL FOR EACH-BASED PRODUCTS */}
      {stockModalOpen && (
        <StockSelectionModal
          isOpen={stockModalOpen}
          toggle={() => setStockModalOpen(false)}
          showCostPriceColumn={true}
          productId={currentProductForStock ? (currentProductForStock.Product_Id || currentProductForStock.product_Id) : null}
          productName={currentProductForStock ? (currentProductForStock.Product_NameEn || currentProductForStock.product_NameEn) : ''}
          requestedQuantity={1}
          onStockSelected={(selectedItems) => handleConfirmStockSelection(selectedItems)}
          branchId={selectedBranch ? selectedBranch.value : null}
          businessId={selectedBusiness ? selectedBusiness.value : null}
          customFetchStock={getStockForExpiredReturn}
        />
      )}

      {/* VIEW DETAILS MODAL */}
      {viewModalOpen && selectedViewReturn && (
        <Modal isOpen={viewModalOpen} toggle={() => setViewModalOpen(false)} size="lg">
          <ModalHeader toggle={() => setViewModalOpen(false)}>
            Expired Return Voucher: {selectedViewReturn.ExpiredReturn_No || selectedViewReturn.expiredReturn_No}
          </ModalHeader>
          <ModalBody>
            <Row className="mb-3">
              <Col md={6}><strong>Date:</strong> {new Date(selectedViewReturn.ExpiredReturn_Date || selectedViewReturn.expiredReturn_Date).toLocaleDateString()}</Col>
              <Col md={6}><strong>Branch:</strong> {selectedViewReturn.Branch_NameEn || selectedViewReturn.branch_NameEn || selectedViewReturn.Branch_Id || selectedViewReturn.branch_Id}</Col>
              <Col md={6}><strong>Supplier:</strong> {selectedViewReturn.Supplier_NameEn || selectedViewReturn.supplier_NameEn || '-'}</Col>
              <Col md={6}><strong>Total Amount:</strong> {parseFloat(selectedViewReturn.TotalAmount || selectedViewReturn.totalAmount || 0).toFixed(2)}</Col>
              <Col md={12} className="mt-2"><strong>Remarks:</strong> {selectedViewReturn.Remarks || selectedViewReturn.remarks || 'N/A'}</Col>
            </Row>
            <Table className="table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Cost Price</th>
                  <th>Discount</th>
                  <th>Tax</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(selectedViewReturn.Details || selectedViewReturn.details || []).map((det, idx) => (
                  <tr key={idx}>
                    <td>{det.Product_NameEn || det.product_NameEn || det.Product_Name || det.product_Name || (det.Product_Id ? `Product #${det.Product_Id}` : (det.product_Id ? `Product #${det.product_Id}` : '-'))}</td>
                    <td>{parseFloat(det.Quantity || det.quantity || 0).toFixed(2)}</td>
                    <td>{parseFloat(det.CostPrice || det.costPrice || 0).toFixed(2)}</td>
                    <td>{parseFloat(det.DiscountAmount || det.discountAmount || 0).toFixed(2)}</td>
                    <td>{parseFloat(det.TaxAmount || det.taxAmount || 0).toFixed(2)}</td>
                    <td>{parseFloat(det.TotalAmount || det.totalAmount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setViewModalOpen(false)}>Close</Button>
          </ModalFooter>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ExpiredReturnPage;
